
import { chain, find, findIndex, first } from 'lodash';
import { createSelector } from 'reselect';
import { mediaTypeDefinitions, programmingMedia } from '../../components/organisms/forms/LicenseBuilder/config';
import { getFormattedDateFromGraphQL } from '../../utils/date';

export const getTermsState = ({ license }: IStoreState) => license;
export const getUsages = (state: ITermsState) => state.usages;
export const getUsageByIndex = (index: number) =>
  createSelector<ITermsState, ILicenseUsage[], ILicenseUsage | undefined>(
    getUsages,
    (usages) => index < usages.length ? usages[index] : undefined,
  );
export const getUsageById = (id: string) =>
  createSelector<ITermsState, ILicenseUsage[], ILicenseUsage | undefined>(
    getUsages,
    (usages) => find(usages, { id }),
  );
export const getUsageIndexById = (id: string) =>
  createSelector<ITermsState, ILicenseUsage[], number>(
    getUsages,
    (usages) => findIndex(usages, { id }),
  );

export const setComponentForUsage = (
  index: number,
  component: LicenseComponentUsageParamName,
  value: ILicenseComponent) => createSelector(
    getUsageByIndex(index),
    (usage: ILicenseUsage) => {
      const newUsage: ILicenseUsage = { ...usage };
      newUsage[component] = value;
      newUsage.id = generateUsageId(newUsage, index);
      return newUsage;
    },
  );

export function isLicenseUsageValid({ term, spot_count, contextual_param, contextual_param_type }: ILicenseUsage) {
  if (!!term && !!spot_count && !!contextual_param && !!contextual_param_type) {
    if (typeof contextual_param === 'object' && (contextual_param as any[]).length > 0) {
      return true;
    }
  }

  if (!term && !spot_count && !!contextual_param && !!contextual_param_type) {
    if (typeof contextual_param === 'object' && (contextual_param as any[]).length > 0) {
      return true;
    }
  }

  return false;
}

export function isLicenseValid(usages: ILicenseUsage[]) {
  return !!usages.length && usages.filter(isLicenseUsageValid).length === usages.length;
}

export function isValidSessionLicense(licenseState: ITermsState) {
  return licenseState.isValid && !!licenseState.scope && licenseState.scope.type === 'Session';
}

export function getContextualParamAsString({ contextual_param, contextual_param_type }: ILicenseUsage) {
  let contextualParamStr = '';

  if (contextual_param && contextual_param.length) {
    switch (contextual_param_type) {
      case 'BroadcastTerritory':
        contextualParamStr = contextual_param.map(({ name }) => name).join(', ');
        break;
      case 'AudienceSize':
        contextualParamStr = `Audience of ${(contextual_param[0] as ILicenseComponent).name}`;
        break;
      case 'ProducerLocation':
        contextualParamStr = `based in ${(contextual_param[0] as ILicenseComponent).name}`;
        break;
      case 'ProductionBudget':
        contextualParamStr = `Production Budget in USD of ${(contextual_param[0] as ILicenseComponent).name}`;
        break;
      case 'EpisodeBudget':
        contextualParamStr = `Episode Budget in USD of ${(contextual_param[0] as ILicenseComponent).name}`;
        break;
    }
  }

  return contextualParamStr;
}

export function getContextualParamTitle({ contextual_param_type }: ILicenseUsage) {
  switch (contextual_param_type) {
    case 'BroadcastTerritory':
      return 'Broadcast Territory';
    case 'AudienceSize':
      return 'Audience Size';
    case 'ProducerLocation':
      return 'Producer Location';
    case 'ProductionBudget':
      return 'Film Production Budget';
    case 'EpisodeBudget':
      return 'Episode Budget';
    default:
      return '';
  }
}

export function generateUsageId(usage: ILicenseUsage, index: number) {
  let id = `${usage.medium.id}${index}`;

  if (usage.term) {
    id += usage.term.id;
  }
  if (usage.spot_count) {
    id += usage.spot_count.id;
  }
  if (usage.contextual_param) {
    const contextParamIds = usage.contextual_param.reduce((acc, { id: paramId }) => `${acc}${paramId}`, '');
    id += contextParamIds;
  }

  return id;
}

export function usageToString(usage: ILicenseUsage, excludeMedium?: boolean) {
  const {
    contextual_param,
    medium,
    spot_count,
    term,
    airDate,
  } = usage;

  let result = excludeMedium ? 'No parameters yet...' : medium.name;
  if (term) {
    if (excludeMedium) {
      result = term.name;
    } else {
      result += `, ${term.name}`;
    }

    if (spot_count) {
      const spotType = medium.id === 2006 ? 'spot' : 'film';
      const plural = parseInt(spot_count.name, 10) > 1 ? 's' : '';
      result += `, ${spot_count.name} ${spotType}${plural}`;

      if (contextual_param && contextual_param.length) {
        result += `, ${getContextualParamAsString(usage)}`;
      }
    }
  }

  if (programmingMedia.indexOf(medium.id) > -1 && contextual_param && contextual_param.length) {
    const paramStr = getContextualParamAsString(usage);
    result = excludeMedium ? paramStr : `${medium.name}, ${paramStr}`;
  }

  if (airDate) {
    result += ` (${getFormattedDateFromGraphQL(airDate)})`;
  }

  return result;
}

export function getLicenseString(usages: ILicenseUsage[]) {
  return usages.map((usage) => usageToString(usage)).join(' + ');
}

export function licenseUsagesToSearchParams(usages: ILicenseUsage[]) {
  return usages.map<GQL.ILicenseUsageParamsInput>((usage) => {
    const { medium, term, spot_count, contextual_param, contextual_param_type } = usage;
    return {
      medium: medium.id,
      term: term && term.id,
      spot_count: spot_count && spot_count.id,
      contextual_param: contextual_param!.map((component) => component.id),
      contextual_param_type: contextual_param_type!,
    };
  });
}

function findLicenseComponentById(component: LicenseComponentConnection, id: number | null | undefined) {
  if (component.edges) {
    return chain<LicenseComponentEdge[]>(component.edges)
      .flatMap(({ node }) => node)
      .filter({ internal_id: id })
      .thru<LicenseComponent | false>((results) => !!results.length && first(results)!)
      .value();
  }

  return false;
}

function getLicenseComponentObjectFromId(
  connection: LicenseComponentConnection,
  needle: number | null | undefined,
): ILicenseComponent | undefined {
  const component = findLicenseComponentById(connection, needle);
  return component && needle ? {
    id: needle,
    name: component.name,
  } : undefined;
}

export function getLicenseUsagesFromQueryString({ components, params }: ISetLicenseFromQueryStringActionPayload) {
  const {
    audience_sizes,
    broadcast_territories,
    media,
    producer_locations,
    spot_counts,
    terms,
    production_budgets,
    episode_budgets,
  } = components;

  return chain(params)
    .map((param) => {
      type UnsafeLicenseComponent = ILicenseComponent | undefined;
      const medium: UnsafeLicenseComponent = getLicenseComponentObjectFromId(media!, param.medium);
      const term: UnsafeLicenseComponent = getLicenseComponentObjectFromId(terms!, param.term);
      const spotCount: UnsafeLicenseComponent =
        getLicenseComponentObjectFromId(spot_counts!, param.spot_count);
      const cxParamType = param.contextual_param_type;
      const cxParams: UnsafeLicenseComponent[] = chain(param.contextual_param)
        .map((id) => [
          getLicenseComponentObjectFromId(audience_sizes!, id),
          getLicenseComponentObjectFromId(broadcast_territories!, id),
          getLicenseComponentObjectFromId(producer_locations!, id),
          getLicenseComponentObjectFromId(production_budgets!, id),
          getLicenseComponentObjectFromId(episode_budgets!, id),
        ])
        .thru((contextualParams) => {
          const result: ILicenseComponent[] = [];
          contextualParams.forEach((possibilities) => {
            possibilities.forEach((possibility) => {
              if (possibility) {
                result.push(possibility);
              }
            });

          });
          return result;
        })
        .value();

      if (medium && cxParamType && cxParams.every((value) => !!value)) {
        const usage: ILicenseUsage = {
          contextual_param_type: cxParamType as LicenseComponentType,
          contextual_param: (cxParams as ILicenseComponent[]),
          medium,
          spot_count: spotCount,
          term,
        };

        return usage;
      }

      return undefined;
    })
    .thru((u) => {
      const validUsages: ILicenseUsage[] = [];
      u.forEach((usage, index) => {
        if (!!usage) {
          usage.id = generateUsageId(usage, index);
          validUsages.push(usage);
        }
      });

      return validUsages;
    })
    .value();
}

// FIXME: this function is pretty much a copy paste of the function above :/
// tslint:disable-next-line:max-line-length
export function setLicenseFromParams(components: GQL.ILicenseComponents, params: Array<GQL.ILicenseUsageParams | GQL.ILicenseUsageParamsInput>) {
  const {
    audience_sizes,
    broadcast_territories,
    media,
    producer_locations,
    spot_counts,
    terms,
    episode_budgets,
    production_budgets,
  } = components;

  const usages =
    chain(params)
      .map((param) => {
        type UnsafeLicenseComponent = ILicenseComponent | undefined;
        const medium: UnsafeLicenseComponent = getLicenseComponentObjectFromId(media!, param.medium);
        const term: UnsafeLicenseComponent = getLicenseComponentObjectFromId(terms!, param.term);
        const spotCount: UnsafeLicenseComponent =
          getLicenseComponentObjectFromId(spot_counts!, param.spot_count);
        const cxParamType = param.contextual_param_type;
        const cxParams: UnsafeLicenseComponent[] = chain(param.contextual_param)
          .map((id) => [
            getLicenseComponentObjectFromId(audience_sizes!, id),
            getLicenseComponentObjectFromId(broadcast_territories!, id),
            getLicenseComponentObjectFromId(producer_locations!, id),
            getLicenseComponentObjectFromId(production_budgets!, id),
            getLicenseComponentObjectFromId(episode_budgets!, id),
          ])
          .thru((contextualParams) => {
            const result: ILicenseComponent[] = [];
            contextualParams.forEach((possibilities) => {
              possibilities.forEach((possibility) => {
                if (possibility) {
                  result.push(possibility);
                }
              });

            });
            return result;
          })
          .value();

        if (medium && cxParamType && cxParams.every((value) => !!value)) {
          const usage: ILicenseUsage = {
            contextual_param_type: cxParamType as LicenseComponentType,
            contextual_param: (cxParams as ILicenseComponent[]),
            medium,
            spot_count: spotCount,
            term,
          };

          return usage;
        }

        return undefined;
      })
      .thru((convertedUsages) => {
        const validUsages: ILicenseUsage[] = [];
        convertedUsages.forEach((u) => {
          if (!!u) {
            // tslint:disable-next-line:max-line-length
            u.id = `${u.medium.id}${u.spot_count && u.spot_count!.id}${u.term && u.term!.id}${u.contextual_param!.map(({ id }) => id)}`;
            validUsages.push(u);
          }
        });

        return validUsages;
      })
      .value();

  return usages;
}

export function generateLicenseUsageFromComponents(
  components: GQL.IStandardLicenseUsageComponent[],
  id?: string,
  airDate?: string,
): ILicenseUsage {
  /* tslint:disable:no-object-literal-type-assertion */
  const usageId = id || components.map((comp) => comp.component_id).join('');

  return components.reduce<ILicenseUsage>((acc, component) => {
    const type = component.type as LicenseComponentType;
    const licenseComponent: ILicenseComponent = {
      id: component.component_id!,
      name: component.name!,
    };
    switch (type) {
      case 'SpotCount':
        acc.spot_count = licenseComponent;
        break;
      case 'Medium':
        const { label } = find(mediaTypeDefinitions, { id: licenseComponent.id as LicenseMediaTypeIdentifier })!;
        if (label) {
          licenseComponent.name = label;
        }
        acc.medium = licenseComponent;
        acc.id = usageId;
        acc.airDate = airDate;
        break;
      case 'Term':
        acc.term = licenseComponent;
        break;
      case 'AudienceSize':
      case 'BroadcastTerritory':
      case 'ProducerLocation':
      case 'EpisodeBudget':
      case 'ProductionBudget':
        if (acc.contextual_param) {
          acc.contextual_param.push(licenseComponent);
        } else {
          acc.contextual_param = [licenseComponent];
          acc.contextual_param_type = type;
        }
        break;
    }
    return acc;
  }, {} as ILicenseUsage);
}

export const getLicenseParams = createSelector(
  getTermsState,
  (license) => {
    return isValidSessionLicense(license) ? licenseUsagesToSearchParams(license.usages) : undefined;
  },
);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/redux/helpers/license.ts